import Vuex from 'vuex'
import axios, {AxiosRequestConfig} from "axios"
import createPersistedState from "vuex-persistedstate"
import {Locales} from "@/i18n/locales"
import {i18n} from "@/main"
import {FungusRecordField, newRecord, newSite} from "@/config/config-fungus"
import {updateRecordURL, saveRecordURL, getRecordByIdURL, deleteRecordURL} from "@/config/app-settings"
import router from "@/router";
import {toUrlParams} from "@/store/data-transformer";

export type User = {
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    street: string,
    streetNo: string,
    zip: string,
    city: string,
    country: number,
    certificates: number[],
    phoneNo: string,
    selfEstimation: number
    imageUploadPermission: number
    restrPublication: number
    userNameRight: number
    personId: number
}

const emptyFungusRecordForm = {
    site: [] as FungusRecordField[],
    records: [] as FungusRecordField[][],
}

const state = {
    token: '',
    tokenTimestamp: null as number | null,
    user: {} as User,
    emailChanged: false as Boolean,
    language: Locales.DE,
    selectedRecordHeaders: [],
    selectedValidationRecordHeaders: [],
    fungusRecordForm: Object.assign({}, emptyFungusRecordForm),
    formState: 'new' as 'new' | 'editing' | 'editingUnsaved',
    notification: '' as string,
    menubarClicked: false as Boolean,
    substrateCoarse: null as number | null,
    errors: {}
}

const persistedState = createPersistedState({
    paths: ['token', 'tokenTimestamp', 'user', 'language', 'selectedRecordHeaders', 'selectedValidationRecordHeaders']
})

export const clearFungusForm = function (formField: FungusRecordField[]) {
    formField.forEach((value) => {
        // special handling on non null config values
        if (["taxonReferenceSuggested"].includes(value.name)) {
            // @ts-ignore
            value.value.forEach((val: FungusRecordField) => {
                val.value = null
            })
        } else if (["images", "specimens", "coords"].includes(value.name)) {
            value.value = []
        } else {
            value.value = null
        }
    });
    return formField

}

export default new Vuex.Store({
    state,
    getters: {
        loggedIn: state => {
            return state.token.length > 0
        },
        personId: state => {
            return state.user.personId
        },
        fullName: state => {
            return state.user.firstName + ' ' + state.user.lastName
        },
        formState: state => {
            return state.formState
        },
        siteIsValid: state => {
            return state.fungusRecordForm.site.every(field => !field.required || (field.required && field.value !== null && field.value !== ''))
        },
        recordsAreValid: state => {
            return state.fungusRecordForm.records.every(fungusRecord => fungusRecord.every(field => !field.required || (field.required && field.value !== null)))
        }
    },
    mutations: {
        ['setLang'](state, payload: Locales) {
            i18n.global.locale = payload
            state.language = payload
        },
        ['setToken'](state, payload: string) {
            // set timestamp for session time out an logging out user when token expires
            state.token = payload;
            state.tokenTimestamp = Date.now()
        },
        ['setSelectedRecordHeaders'](state, payload: any) {
            state.selectedRecordHeaders = payload;
        },
        ['setSelectedValidationRecordHeaders'](state, payload: any) {
            state.selectedValidationRecordHeaders = payload;
        },
        ['setUser'](state, payload) {
            state.user = payload
        },
        ['setEmailChanged'](state, payload) {
            state.emailChanged = payload
        },
        ['setSubstrateCoarse'](state, payload) {
            state.substrateCoarse = payload
        },
        ['setFormState'](state, payload) {
            state.formState = payload
        },
        ['setFungusRecordForm'](state, payload) {
            Object.assign(state.fungusRecordForm, payload)
        },
        ['addFungusRecord'](state, payload: FungusRecordField[]) {
            state.fungusRecordForm.records.push(clearFungusForm(payload))
        },
        ['removeFungusRecord'](state, idx: number) {
            state.fungusRecordForm.records.splice(idx, 1)
        },
        ['finalDeletionOfFungusRecord'](state, recordId: number) {
            axios.delete(deleteRecordURL + '?recordId=' + recordId).then((res) => {
                    console.log("response ", res)
                }
            ).catch((e) => {
                console.log("error ", e)
            })
        },
        ['setFungusRecordValue'](state, payload: { idx: number, key: string, value: any }) {
            const keys = payload.key.split('.')
            const fungusRecord = state.fungusRecordForm.records[payload.idx]
            let field = fungusRecord.find(field => field.name === keys[0])
            if (keys.length === 2 && field !== undefined && field !== null) {
                // @ts-ignore
                field = field.value.find(f => f.name === keys[1])
            }
            // @ts-ignore
            field.value = payload.value
        },
        ['setSiteValue'](state, payload: { key: string, value: any }) {
            const keys = payload.key.split('.')
            let field = state.fungusRecordForm.site.find(field => field.name === keys[0])
            if (keys.length === 2 && field !== undefined && field !== null) {
                // @ts-ignore
                field = field.value.find(f => f.name === keys[1])
            }
            // @ts-ignore
            field.value = payload.value
        },
        ['setNotification'](state, msg: string) {
            state.notification = msg
        },
    },
    actions: {
        ['submitFungusRecordForm']({state, dispatch}) {
            const data = toUrlParams(state.fungusRecordForm)
            const config: AxiosRequestConfig = {
                method: state.formState == 'editing' ? 'put' : 'post',
                url: state.formState == 'editing' ? updateRecordURL : saveRecordURL,
                headers: {
                    'Content-Type': 'application/json'
                },
                data: JSON.stringify(data)
            }
            console.log("submitFungusRecordForm - ", data)
            return axios(config)
                .then(() => {
                    dispatch('deleteFungusRecordForm')
                    state.formState = 'new'
                    return 'Fundmeldung wurde erfolgreich übermittelt! Besten Dank!'
                })
        },
        ['edit']({commit}, payload: any) {
            state.formState = 'editing'

            const fungusRecordForm = {
                site: JSON.parse(JSON.stringify(newSite())),
                records: [JSON.parse(JSON.stringify(newRecord()))]
            }
            axios.get(getRecordByIdURL + '?recordId=' + payload)
                .then(res => {
                    console.log("BACKEND result: ", res)
                    const result = res.data
                    result.coords = [result.x, result.y]
                    fungusRecordForm.site.forEach((field: FungusRecordField) => {
                        if (field.name === 'siteId') {
                            field.value = result.siteId
                        } else if (result[field.name]) {
                            field.value = result[field.name]
                        } else {
                            console.log(">>null field value", field.name, res.data)
                        }
                    })
                    res.data.records.forEach((savedFungusRecord: any, i: number) => {
                        const newFungusRecord = newRecord()
                        newFungusRecord.forEach((field: FungusRecordField) => {
                            if (savedFungusRecord[field.name]) {
                                if (field.name === 'recordId') {
                                    field.value = savedFungusRecord.recordId
                                } else if (field.name === 'taxonReferenceSuggested') {
                                    // @ts-ignore
                                    field.value.find(f => f.name === 'ifFlorAppReferenceWork').value = savedFungusRecord[field.name].ifFlorAppReferenceWork
                                    // @ts-ignore
                                    field.value.find(f => f.name === 'taxonId').value = savedFungusRecord[field.name].taxonId
                                    // @ts-ignore
                                    field.value.find(f => f.name === 'suggestedTaxonName').value = savedFungusRecord[field.name].suggestedTaxonName
                                    // @ts-ignore
                                    field.value.find(f => f.name === 'suggestedTaxonPublication').value = savedFungusRecord[field.name].suggestedTaxonPublication
                                    // @ts-ignore
                                    // field.value.find(f => f.name === 'suggestedTaxonDescription').value = savedFungusRecord[field.name].suggestedTaxonDescription
                                    // @ts-ignore
                                    field.value.find(f => f.name === 'suggestedTaxonComment').value = savedFungusRecord[field.name].suggestedTaxonComment
                                } else {
                                    // @ts-ignore
                                    field.value = savedFungusRecord[field.name]
                                }
                            } else {
                                console.log(">>", field.name, savedFungusRecord[field.name])
                            }
                        })
                        fungusRecordForm.records[i] = newFungusRecord
                    })
                    commit('setFungusRecordForm', fungusRecordForm)
                    router.push({name: 'Form'})
                })
        },
        ['addFungusRecord']({commit}) {
            commit('addFungusRecord', newRecord())
        },
        ['newFungusRecord']({commit}) {
            const payload = {site: newSite(), records: [newRecord(),]}
            commit('setFungusRecordForm', payload)
        },
        ['deleteFungusRecordForm']({commit}) {
            commit('setFungusRecordForm', emptyFungusRecordForm)
        },
        ['login']({commit}, payload: { token: string; user: any }) {
            commit('setToken', payload.token)
            axios.defaults.headers.common['Authorization'] = 'Bearer ' + payload.token
            commit('setUser', payload.user)
        },
        ['logout']({commit}) {
            commit('setToken', '')
            state.tokenTimestamp = 0
            axios.defaults.headers.common['Authorization'] = ''
        },
        ['notify']({commit}, msg: string) {
            commit('setNotification', msg)
            setTimeout(() => commit('setNotification', ''), 5000)
        },
        ['validate']({state}, errors: {}) {
            state.errors = errors
        }
    },
    plugins: [persistedState]
})